import { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(() => {});

// useToast() returns a function: toast(message, type?) where type is
// 'success' | 'error' | 'info' (default 'info'). Also exposes .success / .error / .info helpers.
export function useToast() {
  return useContext(ToastContext);
}

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((message, type = 'info') => {
    if (!message) return;
    const id = nextId++;
    const text = typeof message === 'string' ? message : (message?.message || String(message));
    setToasts((prev) => [...prev, { id, text, type }]);
    // Errors linger a little longer so they can be read.
    const ttl = type === 'error' ? 6000 : 3500;
    setTimeout(() => remove(id), ttl);
  }, [remove]);

  // Attach convenience helpers to the callable toast function.
  const toast = useCallback((message, type) => push(message, type), [push]);
  toast.success = (m) => push(m, 'success');
  toast.error = (m) => push(m, 'error');
  toast.info = (m) => push(m, 'info');

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-auto pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const STYLES = {
  success: { border: 'border-green-500/60',  icon: '✓', iconColor: 'text-green-400' },
  error:   { border: 'border-red-500/60',    icon: '✕', iconColor: 'text-red-400' },
  info:    { border: 'border-orange-500/60', icon: 'ℹ', iconColor: 'text-orange-400' },
};

function ToastItem({ toast, onClose }) {
  const s = STYLES[toast.type] || STYLES.info;
  return (
    <div
      onClick={onClose}
      className={`pointer-events-auto cursor-pointer bg-gray-800 border ${s.border} rounded-lg shadow-xl px-4 py-3 flex items-start gap-3 animate-[fadeIn_0.15s_ease-out]`}
    >
      <span className={`text-sm font-bold ${s.iconColor} mt-0.5`}>{s.icon}</span>
      <span className="text-sm text-gray-100 flex-1 break-words">{toast.text}</span>
    </div>
  );
}
